/* ============================================================================
   Panorama. La vista que responde la pregunta de portada: ¿cuánto cuesta hoy
   alimentar a una persona un mes, y hacia dónde va?

   · Un solo número héroe, y arrastrar la gráfica lo cambia al mes bajo el cursor
     (el encabezado sigue al dedo, como en Bolsa).
   · Los KPI son tarjetas de estadística, no gráficas de una barra.
   · La escala Y no arranca en cero: la canasta se mueve pocos puntos al mes y
     anclar en cero aplanaría la señal. El eje siempre rotula sus valores.
   ========================================================================== */

import { useMemo, useState } from 'react'
import { LineChart, type ChartSeries } from '../charts/LineChart'
import { StatTile } from '../components/StatTile'
import { Delta } from '../components/Delta'
import { Segmented } from '../components/Segmented'
import { Callout, HeroFigure, Section, SourceChip } from '../components/ui'
import {
  AMBITO_COLOR,
  AMBITO_LABEL_F,
  AMBITO_OPTIONS,
  DEFAULT_HOUSEHOLD_SIZE,
  SERIES,
  canastaAlimentaria,
  canastaAmpliada,
  dataset,
  series,
  type Ambito,
} from '../data/dataset'
import {
  DAYS_PER_MONTH,
  RANGE_OPTIONS,
  availableRanges,
  changeOver,
  sliceRange,
  tail,
  valueAtOrLast,
  type RangeKey,
} from '../data/derive'
import { currency, currency0, formatPeriodLong, formatPeriodTitle, number } from '../lib/format'
import type { ViewId } from '../App'

export function Panorama({
  ambito,
  onAmbitoChange,
  onNavigate,
}: {
  ambito: Ambito
  onAmbitoChange: (value: Ambito) => void
  onNavigate: (view: ViewId) => void
}) {
  const [range, setRange] = useState<RangeKey>('60m')
  const [scrubbed, setScrubbed] = useState<string | null>(null)

  const alimentaria = series(canastaAlimentaria(ambito))
  const otraAlimentaria = series(canastaAlimentaria(ambito === 'urbano' ? 'rural' : 'urbano'))
  const ampliada = series(canastaAmpliada(ambito))
  const salario = series(SERIES.salarioMinimoDiario)

  const ranges = useMemo(
    () => RANGE_OPTIONS.filter((option) => availableRanges(alimentaria.points).includes(option.value)),
    [alimentaria.points],
  )

  const chartSeries = useMemo<ChartSeries[]>(() => {
    const urbano = series(SERIES.canastaAlimentariaUrbana)
    const rural = series(SERIES.canastaAlimentariaRural)
    return [
      {
        id: 'urbano',
        label: 'Canasta alimentaria urbana',
        shortLabel: 'Urbana',
        color: AMBITO_COLOR.urbano,
        points: sliceRange(urbano.points, range),
      },
      {
        id: 'rural',
        label: 'Canasta alimentaria rural',
        shortLabel: 'Rural',
        color: AMBITO_COLOR.rural,
        points: sliceRange(rural.points, range),
      },
    ]
  }, [range])

  const shown = valueAtOrLast(alimentaria.points, scrubbed)
  const monthly = changeOver(alimentaria.points, 1)
  const annual = changeOver(alimentaria.points, 12)
  const fiveYear = changeOver(alimentaria.points, 60)

  const latestBasket = alimentaria.points[alimentaria.points.length - 1]

  /* El salario tiene que ser el del MISMO mes que la canasta. La tabla de
     CONASAMI se mantiene a mano y se corta en silencio si falta el año: dividir
     el último salario disponible entre la canasta de un mes posterior
     subestimaría el poder de compra justo el mes en que se usa para planear.
     Sin coincidencia no hay cifra: null, y la tarjeta lo dice. */
  const wageAtBasketPeriod = latestBasket
    ? salario.points.find((p) => p.period === latestBasket.period)
    : undefined
  const monthlyWage = wageAtBasketPeriod ? wageAtBasketPeriod.value * DAYS_PER_MONTH : null
  const householdBasket = latestBasket ? latestBasket.value * DEFAULT_HOUSEHOLD_SIZE : null
  const basketsPerWage =
    monthlyWage !== null && householdBasket !== null && householdBasket > 0
      ? monthlyWage / householdBasket
      : null

  const ampliadaLatest = ampliada.points[ampliada.points.length - 1]
  const ampliadaAnnual = changeOver(ampliada.points, 12)
  const otraLatest = otraAlimentaria.points[otraAlimentaria.points.length - 1]
  /* La resta exige el MISMO mes. Las dos series vienen del mismo indicador y hoy
     avanzan juntas, pero nada lo garantiza: si una se publicara antes, la brecha
     mezclaría dos meses y la sección —que promete explícitamente «las dos últimas
     describen ese mismo mes»— estaría diciendo algo falso sin que nada lo delate.
     Es el mismo error que ya se coló al dividir canasta entre salario. */
  const brecha =
    latestBasket && otraLatest && latestBasket.period === otraLatest.period
      ? ambito === 'urbano'
        ? latestBasket.value - otraLatest.value
        : otraLatest.value - latestBasket.value
      : null

  const isScrubbing = scrubbed !== null && shown !== null && shown.period !== latestBasket?.period

  return (
    <>
      <header className="view-head">
        <p className="t-micro view-head__eyebrow">Panorama</p>
        <h1 className="t-title1">
          Cuánto cuesta alimentar a una persona un mes en México
        </h1>
        <p className="t-body ink-2">
          La canasta alimentaria es el costo mínimo de los alimentos que una persona necesita al mes.
          Quien gana menos que esta cifra no puede comprar la comida que requiere ni gastando en ella
          todo su ingreso. El INEGI la publica cada mes; aquí está su historia completa desde 1992.
        </p>
      </header>

      <div className="hero-card">
        <div className="hero-card__aside">
          <HeroFigure
            value={shown?.value ?? 0}
            format={(value) => currency(value, 2)}
            immediate={scrubbed !== null}
            caption={
              isScrubbing
                ? `Canasta alimentaria ${AMBITO_LABEL_F[ambito]} en ${formatPeriodLong(shown!.period)}`
                : `Canasta alimentaria ${AMBITO_LABEL_F[ambito]} · ${formatPeriodTitle(dataset.latestPeriod)}`
            }
          >
            <span className="t-footnote ink-3">por persona al mes</span>
          </HeroFigure>

          {!isScrubbing ? (
            <div className="hero-deltas">
              {monthly ? (
                <div className="hero-delta">
                  <Delta value={monthly.percent} size="md" />
                  <span className="t-caption ink-3">
                    vs. {formatPeriodTitle(monthly.from.period).toLowerCase()}
                  </span>
                </div>
              ) : null}
              {annual ? (
                <div className="hero-delta">
                  <Delta value={annual.percent} size="md" />
                  <span className="t-caption ink-3">vs. hace un año</span>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="t-footnote ink-3 hero-scrub-hint">
              Suelte el cursor para volver al último dato.
            </p>
          )}

          <div className="row">
            <Segmented
              options={AMBITO_OPTIONS}
              value={ambito}
              onChange={onAmbitoChange}
              ariaLabel="Ámbito geográfico"
            />
          </div>

          <SourceChip label="INEGI · Líneas de Pobreza" confidence="high" />
        </div>

        <div className="stack">
          <div className="row row--between">
            <Segmented
              options={ranges}
              value={range}
              onChange={setRange}
              ariaLabel="Rango de tiempo"
            />
          </div>
          <LineChart
            series={chartSeries}
            title="Costo mensual de la canasta alimentaria por persona"
            subtitle="Pesos corrientes. Ámbito rural y urbano; el contenido de la canasta difiere entre ambos."
            formatValue={(value) => currency0(value)}
            unitLabel="pesos por persona al mes"
            height={340}
            onScrub={setScrubbed}
            footnote="La escala vertical no arranca en cero para que se vea el movimiento mensual; el eje rotula sus valores."
          />
        </div>
      </div>

      <Section
        title="Los cuatro números que importan"
        description="Las dos primeras comparan el último mes publicado contra el mismo mes del año anterior, y ahí subir es adverso: el mismo ingreso alcanza para menos comida. Las dos últimas describen ese mismo mes sin comparación temporal: la tercera es un cociente y la cuarta, una diferencia en pesos entre la canasta urbana y la rural."
      >
        <div className="grid grid--kpi">
          <StatTile
            label={`Canasta alimentaria ${AMBITO_LABEL_F[ambito]}`}
            value={latestBasket?.value ?? 0}
            format={(value) => currency0(value)}
            unit="por persona al mes"
            delta={annual?.percent}
            deltaSince="vs. hace un año"
            trend={tail(alimentaria.points, 24)}
            accent={AMBITO_COLOR[ambito]}
            emphasis
            note={`Último dato: ${formatPeriodTitle(dataset.latestPeriod).toLowerCase()}`}
          />
          <StatTile
            label="Canasta alimentaria más no alimentaria"
            value={ampliadaLatest?.value ?? 0}
            format={(value) => currency0(value)}
            unit="por persona al mes"
            delta={ampliadaAnnual?.percent}
            deltaSince="vs. hace un año"
            trend={tail(ampliada.points, 24)}
            accent={AMBITO_COLOR[ambito]}
            note="Suma alimentos y no alimentos: la línea de pobreza por ingresos."
          />
          <StatTile
            label={`Canastas de ${DEFAULT_HOUSEHOLD_SIZE} personas que compra un salario mínimo`}
            value={basketsPerWage ?? 0}
            format={(value) => (basketsPerWage === null ? 'sin dato' : number(value, 2))}
            delta={undefined}
            invertDelta
            note={
              monthlyWage !== null && latestBasket
                ? `Salario mínimo mensual de ${currency0(monthlyWage)} contra una canasta familiar de ${currency0(householdBasket ?? 0)}, ambos de ${formatPeriodTitle(latestBasket.period).toLowerCase()}.`
                : 'No hay salario mínimo publicado para el mismo mes que la canasta.'
            }
          />
          <StatTile
            label="Brecha entre la canasta urbana y la rural"
            value={brecha ?? 0}
            format={(value) => currency0(Math.abs(value))}
            unit="por persona al mes"
            note="La canasta urbana incluye más renglones y un gasto mayor en varios de ellos; no todos los precios unitarios son más altos en el ámbito urbano."
          />
        </div>
      </Section>

      <Section
        title="Qué tan rápido se encareció"
        description="Comparar el mismo mes de años distintos evita confundir el ciclo estacional de frutas y verduras con una tendencia."
      >
        <div className="grid grid--2">
          <div className="card card--padded stack stack--tight">
            <p className="t-micro ink-3">Cambio anual</p>
            <p className="figure-lg">{annual ? <Delta value={annual.percent} size="md" /> : '—'}</p>
            <p className="t-footnote ink-2">
              {annual
                ? `De ${currency(annual.from.value)} en ${formatPeriodTitle(annual.from.period).toLowerCase()} a ${currency(annual.to.value)} en ${formatPeriodTitle(annual.to.period).toLowerCase()}: ${currency(Math.abs(annual.absolute))} ${annual.absolute >= 0 ? 'más' : 'menos'} por persona al mes.`
                : 'Sin suficiente historia para calcularlo.'}
            </p>
          </div>
          <div className="card card--padded stack stack--tight">
            <p className="t-micro ink-3">Cambio en cinco años</p>
            <p className="figure-lg">
              {fiveYear ? <Delta value={fiveYear.percent} size="md" /> : '—'}
            </p>
            <p className="t-footnote ink-2">
              {fiveYear
                ? `Alimentar a una familia de ${DEFAULT_HOUSEHOLD_SIZE} cuesta ${currency0(Math.abs(fiveYear.absolute) * DEFAULT_HOUSEHOLD_SIZE)} ${fiveYear.absolute >= 0 ? 'más' : 'menos'} al mes que en ${formatPeriodTitle(fiveYear.from.period).toLowerCase()}.`
                : 'Sin suficiente historia para calcularlo.'}
            </p>
          </div>
        </div>
      </Section>

      <Callout kind="info" title="Cómo leer estas cifras">
        <p>
          La canasta alimentaria es un <strong>umbral</strong>, no el gasto observado de los hogares:
          mide lo que cuesta cubrir el requerimiento de energía de una persona, no lo que cuesta comer
          bien. Tampoco incluye no alimentos.
        </p>
        <p>
          Para ver qué renglón empujó el cambio, vaya a{' '}
          <button type="button" className="link-button" onClick={() => onNavigate('productos')}>
            Productos
          </button>
          . Para traducirlo a una despensa concreta,{' '}
          <button type="button" className="link-button" onClick={() => onNavigate('despensa')}>
            Despensa
          </button>
          .
        </p>
      </Callout>
    </>
  )
}
